import debounce from 'lodash/debounce'
import { viewCartDataLayer } from '@/scripts/datalayer'

import DynamicSectionElement from '@/scripts/dynamic-section-element.js'
import { trapFocus } from '@shopify/theme-a11y'

class Cart extends DynamicSectionElement {
  constructor () {
    super()

    // Bind update method so it can be passed as callback argument
    this.onCartUpdate = this.onCartUpdate.bind(this)

    this.handleQuantityChange = debounce(() => {
      this.updateFromForm(this.querySelector('form'))
    }, 500, { leading: false, trailing: true })

    // Use debounced event handler to update cart quanties
    this.addEventListener('change-quantity', () => {
      this.setAttribute('data-loading', true)
      this.handleQuantityChange()
    })

    if (document.body.classList.contains('template-cart')) {
      viewCartDataLayer()
    }

    this.addEventListener('keyup', (event) => {
      const eventCode = event.code.toUpperCase()
      if (eventCode === 'ESCAPE') {
        this.closeDrawer()
      }

      })
  }

  fetchCart () {
    fetch('?sections=site-header,cart').then(res => res.json()).then(data => {
      const newCartEl = new DOMParser()
        .parseFromString(data.cart, 'text/html')
      this.replaceContent(newCartEl)

      // Replace content for re-rendered site-header section
      const newSiteHeaderEl = new DOMParser()
        .parseFromString(data['site-header'], 'text/html')
      document.querySelector('site-header')
        .replaceContent(newSiteHeaderEl)

      this.setAttribute('data-loading', false)
      this.openDrawer()
    })
  }

  openDrawer (event) {
    if (event && event.preventDefault) {
      if (event.metaKey) {
        return
      }
      event.preventDefault()
    }

    this.classList.add('open')
    const buttonEl = event && event.target ? event.target.closest('a') : ''
    if (buttonEl && buttonEl.classList.contains('js-cart-icon')) {
      buttonEl.setAttribute('aria-expanded', 'true')
    }

    if (document.body.classList.contains('user-is-tabbing')) {
      trapFocus(this, {
        elementToFocus: this.querySelector('.js-close-mini-cart')
      })
    }

    if (!window.location.pathname.includes('/cart')) {
      document.body.classList.add('overflow-hidden')
    }

    viewCartDataLayer(true)
  }

  closeDrawer () {
    this.classList.remove('open')
    document.querySelector('.js-cart-icon').setAttribute('aria-expanded', 'false')
    if (!window.location.pathname.includes('/cart')) {
      document.body.classList.remove('overflow-hidden')
    }

    if (document.body.classList.contains('user-is-tabbing')) {
      trapFocus(document.body, {
        elementToFocus: document.querySelector('.js-cart-icon')
      })
    }
  }

  // Add item to cart using HTML form data
  addItemFromForm (form) {
    const formData = new FormData(form)
    formData.append('sections', 'site-header,cart')
    formData.append('sections_url', window.location.pathname)

    this.setAttribute('data-loading', true)

    return fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    }).then(this.onCartUpdate)
  }

  // Add item to cart
  // let formData = {
  //   'items': [{
  //     'id': id,
  //     'quantity': 1
  //   }],
  //   'sections': 'site-header,cart',
  //   'sections_url': window.location.pathname
  // };

  addItem (formData) {
    this.setAttribute('data-loading', true)

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    }).then(this.onCartUpdate)
  }

  // Sets all cart attributes
  updateAttributes (attributes) {
    return fetch('/cart/update.js', {
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ attributes })
    }).then(response => response.json())
  }

  // Update items in cart using HTML form data
  updateFromForm (form) {
    const formData = new FormData(form)
    formData.append('sections', 'site-header,cart')
    formData.append('sections_url', window.location.pathname)

    this.setAttribute('data-loading', true)

    fetch('/cart/update.js', {
      method: 'POST',
      body: formData
    }).then(this.onCartUpdate)
  }

  changeItem (data) {
    this.setAttribute('data-loading', true)
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...data,
        sections: 'site-header,cart',
        sections_url: window.location.pathname
      })
    }).then(this.onCartUpdate)
  }

  // Remove an item from cart using line item key
  removeItem (key) {
    this.setAttribute('data-loading', true)

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        updates: { [key]: 0 },
        sections: 'site-header,cart',
        sections_url: window.location.pathname
      })
    }).then(this.onCartUpdate)
  }

  // Handle cart API response and update sections with dynamic content
  onCartUpdate (cartResponse) {
    return cartResponse.json().then(({ sections }) => {
      // Replace content for re-rendered cart section
      const newCartEl = new DOMParser()
        .parseFromString(sections.cart, 'text/html')
      this.replaceContent(newCartEl)

      // Replace content for re-rendered site-header section
      const newSiteHeaderEl = new DOMParser()
        .parseFromString(sections['site-header'], 'text/html')
      document.querySelector('site-header')
        .replaceContent(newSiteHeaderEl)

      this.setAttribute('data-loading', false)
      this.openDrawer()
    })
  }
}

if (!customElements.get('shopify-cart')) {
  customElements.define('shopify-cart', Cart)
}
